import React, { useMemo, useState } from 'react'
import { Button, Card, Dropdown, Form, InputGroup, Spinner, Table } from 'react-bootstrap'
import { ExternalLink, Filter, Search } from 'lucide-react'
import { useSubscriptionDevices } from '@data/subscriptions/subscriptions'
import { useInvoiceHistory } from '@data/subscriptions/subscriptions'
import { useOpenPortalSession } from '@data/billing/billing'
import {
  DeviceAlertStatus,
  IInvoiceRow,
  ISubscriptionDeviceRow,
  deriveDeviceStatus,
} from '@data/subscriptions/model/subscriptions.model'
import SubscriptionAlerts, { InvoiceQuickFilter } from './SubscriptionAlerts'
import SharePaymentLinkModal from './SharePaymentLinkModal'
import FinalizeInvoiceModal from './FinalizeInvoiceModal'
import { useAuthStore } from '@store/auth.store'
import { hasPermissions } from '@common/utils/validation/authValidators'

type StatusKey = 'all' | DeviceAlertStatus

const STATUS_PILLS: { key: StatusKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'paid', label: 'Paid' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'restricted', label: 'Restricted' },
  { key: 'pending', label: 'Pending 1st Payment' },
]

const STATUS_BADGE_CLASS: Record<DeviceAlertStatus, string> = {
  healthy: 'sf-status-badge sf-status-badge--healthy',
  paid: 'sf-status-badge sf-status-badge--paid',
  upcoming: 'sf-status-badge sf-status-badge--upcoming',
  overdue: 'sf-status-badge sf-status-badge--overdue',
  restricted: 'sf-status-badge sf-status-badge--restricted',
  pending: 'sf-status-badge sf-status-badge--pending',
  no_subscription: 'sf-status-badge sf-status-badge--neutral',
}

const STATUS_LABEL: Record<DeviceAlertStatus, string> = {
  healthy: 'Healthy',
  paid: 'Paid',
  upcoming: 'Upcoming',
  overdue: 'Overdue',
  restricted: 'Restricted',
  pending: 'Pending 1st Payment',
  no_subscription: 'No Subscription',
}

// Invoice statuses that mean "the customer still owes this".
//   open          — finalized and sent, awaiting payment
//   past_due      — open past its due date
//   uncollectible — written off, but the debt still stands
// Excluded: `paid` (settled), `void` (cancelled — nothing owed), and `draft`
// (not yet finalized, so it has no hosted URL and the customer has not been
// billed for it yet).
const UNPAID_INVOICE_STATUSES = new Set(['open', 'past_due', 'uncollectible'])

const MS_PER_DAY = 1000 * 60 * 60 * 24

function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  return Math.round((new Date(iso).getTime() - Date.now()) / MS_PER_DAY)
}

function describeNextStep(row: ISubscriptionDeviceRow, status: DeviceAlertStatus): string {
  switch (status) {
    case 'restricted':
      return 'Access restricted — settle balance to restore'
    case 'overdue': {
      const days = daysUntil(row.currentPeriodEnd)
      return days !== null && days < 0
        ? `Payment overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`
        : 'Payment overdue'
    }
    case 'upcoming': {
      const days = daysUntil(row.currentPeriodEnd)
      return days !== null
        ? `Renews in ${days} day${days === 1 ? '' : 's'}`
        : 'Renewal upcoming'
    }
    case 'healthy': {
      const days = daysUntil(row.currentPeriodEnd)
      return days !== null
        ? `Next renewal in ${days} days`
        : 'Active'
    }
    case 'paid': {
      // The next term is already settled; show what it is paid through
      // rather than a countdown to a renewal that needs no action.
      const paidThrough = row.paidThrough
        ? new Date(row.paidThrough).toLocaleDateString()
        : null
      return paidThrough ? `Paid through ${paidThrough}` : 'Next term paid'
    }
    case 'pending':
      return 'Awaiting first payment confirmation'
    case 'no_subscription':
      return 'No subscription'
  }
}

/** Amounts arrive in minor units (cents/pence). */
function formatAmount(minorUnits: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100)
}

const INVOICE_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  past_due: 'Past due',
  uncollectible: 'Uncollectible',
}

/**
 * Dropdown label, e.g. "QLGBVQPZ-0037 · €120.00 · 26 Jun 2026 · Past due".
 * The status is included because the list mixes open, past-due and written-off
 * invoices, which are not otherwise distinguishable from the number alone.
 */
function describeInvoice(invoice: IInvoiceRow): string {
  const parts = [invoice.number ?? invoice.invoiceId]
  parts.push(formatAmount(invoice.amountDue, invoice.currency))
  if (invoice.periodStart) {
    parts.push(
      new Date(invoice.periodStart).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    )
  }
  parts.push(INVOICE_STATUS_LABEL[invoice.status] ?? invoice.status)
  return parts.join(' · ')
}

interface Props {
  accountId: number
  onViewInvoices?: (filter: InvoiceQuickFilter) => void
}

const DevicesPaymentsOverview: React.FC<Props> = ({ accountId, onViewInvoices }) => {
  const [search, setSearch] = useState('')
  const [locationId, setLocationId] = useState<number | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<StatusKey>('all')
  const [shareEntityId, setShareEntityId] = useState<number | null>(null)
  const [finalizeTarget, setFinalizeTarget] = useState<ISubscriptionDeviceRow | null>(null)
  const { data: devices, isLoading, isError } = useSubscriptionDevices(accountId)
  const { data: invoices } = useInvoiceHistory(accountId)
  const permissions = useAuthStore((state) => state.permissions)
  const canAccessBillingPortal = hasPermissions(permissions, ['READ:BILLING:ENTITY'])
  const canFinalize = hasPermissions(permissions, ['FINALIZE:INVOICE'])
  const openPortal = useOpenPortalSession()

  /**
   * "Finalize and Add PO" belongs on a device only when there is actually a
   * held draft to act on: the entity bills a PO per invoice (mode 2), the
   * renewal invoice is still a draft, and the user may finalize (SF-APP-010).
   */
  const canFinalizeRow = (row: ISubscriptionDeviceRow): boolean =>
    canFinalize && row.poMode === 2 && !!row.draftInvoiceId

  const rows = useMemo(
    () =>
      (devices ?? []).map((d) => ({
        row: d,
        status: deriveDeviceStatus(d),
      })),
    [devices],
  )

  const statusCounts = useMemo(() => {
    const acc: Record<StatusKey, number> = {
      all: rows.length,
      healthy: 0,
      paid: 0,
      upcoming: 0,
      overdue: 0,
      restricted: 0,
      pending: 0,
      no_subscription: 0,
    }
    for (const { status } of rows) acc[status] += 1
    return acc
  }, [rows])

  const locations = useMemo(() => {
    const map = new Map<number, string>()
    for (const { row } of rows) map.set(row.locationId, row.locationName)
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [rows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(({ row, status }) => {
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (locationId !== 'all' && row.locationId !== locationId) return false
      if (!q) return true
      return (
        (row.deviceName ?? '').toLowerCase().includes(q) ||
        row.locationName.toLowerCase().includes(q) ||
        String(row.deviceId).includes(q)
      )
    })
  }, [rows, statusFilter, locationId, search])

  // Devices grouped under the billing entity that owns their subscription.
  // Entities are kept in a stable alphabetical order so the list doesn't
  // reshuffle as filters change; an entity whose devices are all filtered
  // out drops off entirely rather than rendering an empty section.
  const groups = useMemo(() => {
    const map = new Map<number, { id: number; name: string; rows: typeof filteredRows }>()
    for (const entry of filteredRows) {
      const { billingEntityId, billingEntityName } = entry.row
      let group = map.get(billingEntityId)
      if (!group) {
        group = { id: billingEntityId, name: billingEntityName, rows: [] }
        map.set(billingEntityId, group)
      }
      group.rows.push(entry)
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredRows])

  // Unpaid invoices per subscription, keyed by Stripe subscription ID.
  // Matching on identity rather than on device/location names: names are not
  // unique across billing entities, and a renamed device would orphan its own
  // invoices. One-off invoices carry no subscription and are skipped — they
  // belong to the entity, not to any single device.
  //
  // Newest first, so the most recent unpaid invoice is the default action.
  const openInvoicesBySubscription = useMemo(() => {
    const map = new Map<string, IInvoiceRow[]>()
    for (const inv of invoices ?? []) {
      if (!inv.subscriptionId) continue
      if (!UNPAID_INVOICE_STATUSES.has(inv.status)) continue
      const list = map.get(inv.subscriptionId)
      if (list) list.push(inv)
      else map.set(inv.subscriptionId, [inv])
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const x = a.periodStart ? new Date(a.periodStart).getTime() : 0
        const y = b.periodStart ? new Date(b.periodStart).getTime() : 0
        return y - x
      })
    }
    return map
  }, [invoices])

  const findOpenInvoices = (deviceRow: ISubscriptionDeviceRow): IInvoiceRow[] => {
    if (!deviceRow.stripeSubscriptionId) return []
    return openInvoicesBySubscription.get(deviceRow.stripeSubscriptionId) ?? []
  }

  const handleShare = (deviceRow: ISubscriptionDeviceRow) => {
    setShareEntityId(deviceRow.billingEntityId)
  }

  const invoiceUrl = (invoice: IInvoiceRow): string | null =>
    invoice.hostedInvoiceUrl ?? invoice.invoicePdf ?? null

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" />
      </div>
    )
  }

  if (isError) {
    return <p className="text-danger">Failed to load subscription data.</p>
  }

  return (
    <div>
      <h4 className="mb-3">Devices &amp; Payments</h4>

      <SubscriptionAlerts
        devices={devices}
        accountId={accountId}
        onViewInvoices={onViewInvoices}
      />

      <Card className="sf-filter-card mb-3">
        <Card.Body>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label className="text-muted small fw-semibold">LOCATION</Form.Label>
              <InputGroup>
                <InputGroup.Text><Filter size={14} /></InputGroup.Text>
                <Form.Select
                  value={locationId === 'all' ? 'all' : String(locationId)}
                  onChange={(e) =>
                    setLocationId(e.target.value === 'all' ? 'all' : Number(e.target.value))
                  }
                >
                  <option value="all">All accessible Locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </div>
            <div className="col-md-8">
              <Form.Label className="text-muted small fw-semibold">SEARCH DEVICES</Form.Label>
              <InputGroup>
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Control
                  placeholder="Device ID or location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <span className="text-muted small fw-semibold me-2">STATUS:</span>
            {STATUS_PILLS.map((pill) => (
              <span
                key={pill.key}
                className={`sf-status-pill ${statusFilter === pill.key ? 'active' : ''}`}
                onClick={() => setStatusFilter(pill.key)}
              >
                {pill.label}
                <span className="sf-status-pill__count">{statusCounts[pill.key]}</span>
              </span>
            ))}
          </div>
        </Card.Body>
      </Card>

      {groups.length === 0 ? (
        <Card>
          <Card.Body className="text-center text-muted py-3">No devices found</Card.Body>
        </Card>
      ) : (
        groups.map((group) => (
          <Card key={group.id} className="mb-3">
            <Card.Header className="sf-entity-header">
              <div>
                <div className="sf-entity-header__name">{group.name}</div>
                <div className="sf-entity-header__meta">
                  {group.rows.length} device{group.rows.length === 1 ? '' : 's'}
                </div>
              </div>
              {canAccessBillingPortal && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={openPortal.isPending}
                  onClick={() =>
                    openPortal.mutate({
                      billingEntityId: group.id,
                      returnUrl: window.location.href,
                    })
                  }
                >
                  {/* Only the clicked entity spins — the mutation is shared
                      across all headers, so compare the in-flight ID. */}
                  {openPortal.isPending && openPortal.variables?.billingEntityId === group.id ? (
                    <Spinner size="sm" animation="border" className="me-1" />
                  ) : (
                    <ExternalLink size={14} className="me-1" />
                  )}
                  Open Billing Portal
                </Button>
              )}
            </Card.Header>
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th className="ps-3">Device</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Countdown / Next Step</th>
                  <th>Purchase Order</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map(({ row, status }) => {
                  const openInvoices = findOpenInvoices(row)
                  return (
                    <tr key={row.deviceId}>
                      <td className="fw-semibold ps-3">{row.deviceName ?? row.deviceId}</td>
                      <td>{row.locationName}</td>
                      <td>
                        <span className={STATUS_BADGE_CLASS[status]}>
                          {STATUS_LABEL[status]}
                          {status === 'upcoming' &&
                            (() => {
                              const days = daysUntil(row.currentPeriodEnd)
                              return days !== null ? ` · ${days}d` : ''
                            })()}
                        </span>
                      </td>
                      <td>{describeNextStep(row, status)}</td>
                      <td className="text-muted small">
                        {row.poNumber ?? (canFinalizeRow(row) ? 'No PO assigned' : '—')}
                      </td>
                      <td className="text-end pe-4">
                        {canFinalizeRow(row) && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 me-3"
                            onClick={() => setFinalizeTarget(row)}
                          >
                            Finalize and Add PO
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 me-3"
                          onClick={() => handleShare(row)}
                        >
                          Share Open Invoices
                        </button>
                        {openInvoices.length === 0 ||
                        !openInvoices.some((inv) => invoiceUrl(inv)) ? (
                          <span className="text-muted small">—</span>
                        ) : openInvoices.length === 1 ? (
                          // Single open invoice — link straight to it rather
                          // than making the user open a one-item menu.
                          <a
                            href={invoiceUrl(openInvoices[0])!}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-link btn-sm p-0"
                          >
                            View Open Invoices <ExternalLink size={12} className="ms-1" />
                          </a>
                        ) : (
                          <Dropdown align="end" className="d-inline-block">
                            <Dropdown.Toggle
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                            >
                              View Open Invoices ({openInvoices.length})
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {openInvoices.map((invoice) => (
                                <Dropdown.Item
                                  key={invoice.invoiceId}
                                  href={invoiceUrl(invoice) ?? undefined}
                                  target="_blank"
                                  rel="noreferrer"
                                  disabled={!invoiceUrl(invoice)}
                                >
                                  {describeInvoice(invoice)}
                                  <ExternalLink size={12} className="ms-2" />
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card>
        ))
      )}

      {shareEntityId !== null && (
        <SharePaymentLinkModal
          billingEntityId={shareEntityId}
          show={shareEntityId !== null}
          onHide={() => setShareEntityId(null)}
        />
      )}

      {finalizeTarget?.draftInvoiceId && (
        // Reuses the Invoices tab's modal so both entry points share one
        // validation, permission and PO workflow. The key remounts it per
        // device so the PO input never carries over between rows.
        <FinalizeInvoiceModal
          key={finalizeTarget.draftInvoiceId}
          invoice={{
            invoiceId: finalizeTarget.draftInvoiceId,
            requiresPo: finalizeTarget.poMode === 2,
            poNumber: finalizeTarget.poNumber,
            number: null,
          }}
          accountId={accountId}
          show
          onHide={() => setFinalizeTarget(null)}
        />
      )}
    </div>
  )
}

export default DevicesPaymentsOverview
